<script setup lang="ts">
import getLightness from "~/utils/get-lightness";

export interface CardPlayerJerseyProps {
  playerNumber: number;
  isSelected?: boolean;
  color?: string;
}

const props = withDefaults(defineProps<CardPlayerJerseyProps>(), {
  isSelected: false,
  color: "#D0142C",
});

const emit = defineEmits<{
  click: [];
}>();

const filterId = `filter0_d_${Math.random().toString(36).substr(2, 9)}`;
const unselectedFilterId = `filter0_d_${Math.random().toString(36).substr(2, 9)}`;

const numberColor = computed(() => {
  const lightness = getLightness(props.color);
  return lightness > 50 ? "#1A1C1E" : "#FFFFFF";
});

const handleClick = () => {
  emit("click");
};
</script>

<template>
  <div
    class="relative inline-flex flex-col justify-center items-center cursor-pointer w-[76px] h-[110px] flex-shrink-0"
    @click="handleClick"
  >
    <!-- Default Jersey SVG -->
    <svg
      v-if="!props.isSelected"
      width="76"
      height="100"
      viewBox="0 0 76 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class="absolute left-0 top-[5px]"
    >
      <g :filter="`url(#${unselectedFilterId})`">
        <path
          d="M47.6912 11.8594C47.989 10.6933 49.0738 10.1126 49.8845 10.3535L58.8162 13.0078C59.568 13.2314 60.0659 14.0994 59.8093 15.002C58.8005 18.5509 58.2978 22.3536 58.5037 26.0186L58.5046 26.0283C58.77 30.2326 59.4419 34.6551 63.1179 37.7422C63.6287 38.1713 63.8763 38.6834 63.8767 39.1328L63.9148 85.0352C63.9154 85.793 63.316 86.4044 62.572 86.4229C54.4926 86.6227 45.5572 86.6013 37.657 86.6006H37.656C29.7556 86.6013 20.8198 86.6227 12.74 86.4229C11.9962 86.4042 11.3976 85.7928 11.3982 85.0352L11.4363 39.1328C11.4367 38.6833 11.6849 38.1714 12.196 37.7422C15.8722 34.6551 16.543 30.2325 16.8083 26.0283L16.8093 26.0186C17.0152 22.3536 16.5125 18.5509 15.5037 15.002C15.2471 14.0994 15.745 13.2314 16.4968 13.0078L25.4294 10.3535C26.2401 10.1128 27.325 10.6933 27.6228 11.8594C28.1123 13.7761 28.9062 15.6559 30.0144 17.2852L30.0193 17.291L30.0232 17.2979C31.0448 18.7657 32.3213 20.0477 33.8474 20.9932C36.1796 22.4379 39.1334 22.4379 41.4656 20.9932C42.9918 20.0477 44.2682 18.7657 45.2898 17.2979L45.2947 17.291L45.2986 17.2852C46.4069 15.6559 47.2016 13.7762 47.6912 11.8594Z"
          :fill="props.color"
          stroke="white"
          stroke-width="2.79597"
        />
      </g>
      <defs>
        <filter
          :id="unselectedFilterId"
          x="-0.00219727"
          y="0"
          width="75.3152"
          height="100"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="5" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.0196078 0 0 0 0 0.105882 0 0 0 0 0.266667 0 0 0 0.2 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_257_15678" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_257_15678" result="shape" />
        </filter>
      </defs>
    </svg>

    <!-- Selected Jersey SVG -->
    <svg
      v-else
      width="76"
      height="110"
      viewBox="0 0 76 110"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class="absolute left-0 top-0"
    >
      <g :filter="`url(#${filterId})`">
        <path
          d="M47.6912 21.8594C47.989 20.6933 49.0738 20.1126 49.8845 20.3535L58.8162 23.0078C59.568 23.2314 60.0659 24.0994 59.8093 25.002C58.8005 28.5509 58.2978 32.3536 58.5037 36.0186L58.5046 36.0283C58.77 40.2326 59.4419 44.6551 63.1179 47.7422C63.6287 48.1713 63.8763 48.6834 63.8767 49.1328L63.9148 95.0352C63.9154 95.793 63.316 96.4044 62.572 96.4229C54.4926 96.6227 45.5572 96.6013 37.657 96.6006H37.656C29.7556 96.6013 20.8198 96.6227 12.74 96.4229C11.9962 96.4042 11.3976 95.7928 11.3982 95.0352L11.4363 49.1328C11.4367 48.6833 11.6849 48.1714 12.196 47.7422C15.8722 44.6551 16.543 40.2325 16.8083 36.0283L16.8093 36.0186C17.0152 32.3536 16.5125 28.5509 15.5037 25.002C15.2471 24.0994 15.745 23.2314 16.4968 23.0078L25.4294 20.3535C26.2401 20.1128 27.325 20.6933 27.6228 21.8594C28.1123 23.7761 28.9062 25.6559 30.0144 27.2852L30.0193 27.291L30.0232 27.2979C31.0448 28.7657 32.3213 30.0477 33.8474 30.9932C36.1796 32.4379 39.1334 32.4379 41.4656 30.9932C42.9918 30.0477 44.2682 28.7657 45.2898 27.2979L45.2947 27.291L45.2986 27.2852C46.4069 25.6559 47.2016 23.7762 47.6912 21.8594Z"
          :fill="props.color"
          stroke="#53B25C"
          stroke-width="2.79597"
        />
        <rect x="23.9978" y="8" width="28" height="28" rx="14" fill="#53B25C" />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M45.9978 17.9128L44.3736 16.4L35.5791 24.591L31.622 20.9054L29.9978 22.4181L35.5646 27.6L45.9978 17.9128Z"
          fill="white"
        />
      </g>
      <defs>
        <filter
          :id="filterId"
          x="-0.00219727"
          y="0"
          width="75.3152"
          height="110"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="5" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.0196078 0 0 0 0 0.105882 0 0 0 0 0.266667 0 0 0 0.2 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_257_15972" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_257_15972" result="shape" />
        </filter>
      </defs>
    </svg>

    <!-- Player Number Overlay for Selected State -->
    <div
      v-if="props.isSelected"
      class="absolute inset-0 flex items-center justify-center pointer-events-none"
      style="top: 50%; transform: translateY(-50%);"
    >
      <div class="text-4xl font-bold font-['Spline_Sans'] uppercase leading-none" :style="{ color: numberColor }">
        {{ props.playerNumber }}
      </div>
    </div>

    <!-- Player Number Overlay (only for default state) -->
    <div
      v-if="!props.isSelected"
      class="absolute left-0 top-[5px] w-[76px] h-[100px] flex items-center justify-center pointer-events-none"
    >
      <div class="text-4xl font-bold font-['Spline_Sans'] uppercase leading-none" :style="{ color: numberColor }">
        {{ props.playerNumber }}
      </div>
    </div>
  </div>
</template>
